import { useEffect } from "react";
import { Trash2 } from "lucide-react";
import { api, eur } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const emptyLoc = () => ({
  indirizzo: "", comune: "", provincia: "", cap: "", zona_sismica: "", classe_alluvione: "media",
  materiale: "Muratura", anno_costruzione: "Sconosciuto", numero_piani: "Sconosciuto", piano_rischio: "Piano terra",
  interrati: false, partite: { fabbricato: "", macchinario: "", merci: "", terreni: "" },
});

const Sel = ({ value, onChange, items, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="[&>span]:truncate"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
    <SelectContent>{items.map((it) => <SelectItem key={it.value} value={it.value}>{it.label}</SelectItem>)}</SelectContent>
  </Select>
);
const opts = (arr) => arr.map((m) => ({ value: m, label: m }));

// Una ubicazione del rischio CAT NAT: indirizzo, zona, caratteristiche del fabbricato e partite.
export const CatNatUbicazione = ({ i, loc, config, onChange, onRemove, multi }) => {
  const set = (patch) => onChange((l) => ({ ...l, ...patch }));
  const t = (k) => `catnat-ub${i}-${k}`;
  useEffect(() => {
    const prov = (loc.provincia || "").trim().toUpperCase();
    if (prov.length < 2) return;
    api.get(`/catnat/zona?provincia=${prov}`).then(({ data }) => { if (data.zona_sismica) onChange((l) => ({ ...l, zona_sismica: data.zona_sismica })); }).catch(() => {});
  }, [loc.provincia]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card className="rounded-xl border-border/60 p-5" data-testid={`catnat-ubicazione-${i}`}>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-heading text-lg font-semibold text-forest">{multi ? `Ubicazione ${i + 1}` : "Ubicazione del rischio"}</h2>
        {onRemove && <button onClick={onRemove} data-testid={t("remove")} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" />Rimuovi</button>}
      </div>
      <div className="space-y-4">
        <div className="space-y-1.5"><Label>Indirizzo</Label>
          <Input data-testid={t("indirizzo")} value={loc.indirizzo} onChange={(e) => set({ indirizzo: e.target.value })} placeholder="es. Via Roma 1" /></div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-1.5"><Label>Comune</Label><Input data-testid={t("comune")} value={loc.comune} onChange={(e) => set({ comune: e.target.value })} placeholder="es. Gesturi" /></div>
          <div className="space-y-1.5"><Label>Provincia</Label><Input data-testid={t("provincia")} value={loc.provincia} maxLength={2} onChange={(e) => set({ provincia: e.target.value.toUpperCase() })} placeholder="es. VS" /></div>
          <div className="space-y-1.5"><Label>CAP</Label><Input data-testid={t("cap")} value={loc.cap} onChange={(e) => set({ cap: e.target.value })} placeholder="es. 09020" /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Zona sismica</Label>
            <Sel testid={t("zona")} value={loc.zona_sismica} onChange={(v) => set({ zona_sismica: v })} items={config.zone_sismiche.map((z) => ({ value: z, label: `Zona ${z}${z === "1" ? " (alta)" : z === "4" ? " (bassa)" : ""}` }))} /></div>
          <div className="space-y-1.5"><Label>Classe pericolosità alluvione</Label>
            <Sel testid={t("alluvione")} value={loc.classe_alluvione} onChange={(v) => set({ classe_alluvione: v })} items={config.classi_alluvione.map((c) => ({ value: c.code, label: c.label }))} /></div>
          <div className="space-y-1.5"><Label>Materiale strutture</Label><Sel testid={t("materiale")} value={loc.materiale} onChange={(v) => set({ materiale: v })} items={opts(config.materiali)} /></div>
          <div className="space-y-1.5"><Label>Anno di costruzione</Label><Sel testid={t("anno")} value={loc.anno_costruzione} onChange={(v) => set({ anno_costruzione: v })} items={opts(config.anni_costruzione)} /></div>
          <div className="space-y-1.5"><Label>Numero piani</Label><Sel testid={t("piani")} value={loc.numero_piani} onChange={(v) => set({ numero_piani: v })} items={opts(config.numero_piani)} /></div>
          <div className="space-y-1.5"><Label>Piano del rischio</Label><Sel testid={t("piano-rischio")} value={loc.piano_rischio} onChange={(v) => set({ piano_rischio: v })} items={opts(config.piano_rischio)} /></div>
          <div className="col-span-2 flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5">
            <Label className="cursor-pointer">Presenza di piani interrati</Label>
            <Switch data-testid={t("interrati")} checked={!!loc.interrati} onCheckedChange={(v) => set({ interrati: v })} />
          </div>
        </div>
        <div className="border-t border-border/60 pt-4">
          <div className="mb-2 text-sm font-semibold text-forest">Partite assicurate</div>
          <div className="grid grid-cols-2 gap-4">
            {config.partite.map((p) => (
              <div key={p.code} className="space-y-1.5">
                <Label>{p.label} <span className="text-[11px] text-muted-foreground">(≤ {eur(p.sottolimite)})</span></Label>
                <Input data-testid={t(`partita-${p.code}`)} type="number" min={0} value={loc.partite[p.code]}
                  onChange={(e) => set({ partite: { ...loc.partite, [p.code]: e.target.value } })} placeholder="€ 0" />
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">Terreni e Merci vanno abbinati a Fabbricato o Macchinario; le Merci richiedono anche una polizza Incendio Assimoco.</p>
        </div>
      </div>
    </Card>
  );
};
